import * as React from 'react';
import * as ReactDOM from 'react-dom';

// PACKAGES
import { CSSTransition } from 'react-transition-group';

// ## closeModal function is passed down to dynamic children ##

const getScrollbarWidth = ((): void => {
	// https://stackoverflow.com/a/13382873

	const outer = document.createElement('div');
	outer.style.visibility = 'hidden';
	outer.style.overflow = 'scroll';
	document.body.appendChild(outer);

	const inner = document.createElement('div');
	outer.appendChild(inner);

	const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

	if (outer.parentNode) {
		outer.parentNode.removeChild(outer);
	}

	document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
})();

export interface GlobalModalPropsTypes {
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	className?: string;
	useDefaultStructure?: boolean;
	closeOnOutsideClick?: boolean;
	titleText?: string;
	closeText?: string;
	children?: React.ReactNode;
}

export interface GlobalModalStateTypes {
	isOpen: boolean;
	useDefaultStructure?: boolean;
	closeOnOutsideClick?: boolean;
	titleText?: string;
	closeText?: string;
}

const initialModalState: GlobalModalStateTypes = {
	isOpen: false,
	useDefaultStructure: true,
	closeOnOutsideClick: false,
	titleText: '',
	closeText: 'Close'
};

export type closeModalType = () => void;

export const GlobalModal: React.FC<GlobalModalPropsTypes> = (props) => {
	const el = document.getElementById('modal');

	const [modalState, setModalState] = React.useState<GlobalModalStateTypes>(initialModalState);

	const afterClose = () => {
		document.body.classList.remove('modal-open');
	};

	const closeModal = () => {
		props.setIsOpen(false);

		setModalState({
			...modalState,
			isOpen: false
		});
	};

	const childrenWithProps = React.Children.map(props.children, (child) => {
		if (React.isValidElement(child)) {
			const customProps = { closeModal };
			return React.cloneElement(child, customProps);
		}

		return child;
	});

	React.useEffect(() => {
		if (props.isOpen) {
			document.body.classList.add('modal-open');

			setModalState({
				isOpen: props.isOpen,
				useDefaultStructure:
					props.useDefaultStructure !== undefined ? props.useDefaultStructure : initialModalState.useDefaultStructure,
				closeOnOutsideClick:
					props.closeOnOutsideClick !== undefined ? props.closeOnOutsideClick : initialModalState.closeOnOutsideClick,
				titleText: props.titleText || initialModalState.titleText,
				closeText: props.closeText || initialModalState.closeText
			});
		}

		// on unmount (which can also be a page change)
		return () => {
			afterClose();
		};
	}, [props.isOpen]);

	React.useEffect(() => {
		setModalState((prevState) => ({ ...prevState, titleText: props.titleText || initialModalState.titleText }));
	}, [props.titleText]);

	if (el) {
		return ReactDOM.createPortal(
			<CSSTransition
				in={modalState.isOpen}
				timeout={300}
				classNames="standalone-fade"
				mountOnEnter
				unmountOnExit
				onExited={afterClose}
			>
				<div
					className={'global-modal ' + props.className}
					tabIndex={-1}
					role="dialog"
					aria-labelledby="title"
					onClick={() => {
						modalState.closeOnOutsideClick && closeModal();
					}}
				>
					<div className="modal-content" role="document" onClick={(e) => e.stopPropagation()}>
						{modalState.useDefaultStructure ? (
							<>
								<div className="modal-header">
									<h1 className="modal-title h3" id="title">
										{modalState.titleText}
									</h1>
									<button type="button" className="btn btn-text" onClick={closeModal}>
										{modalState.closeText}
									</button>
								</div>
								<div className="modal-body">{childrenWithProps}</div>
							</>
						) : (
							<>
								<h1 className="sr-only" id="title">
									{modalState.titleText}
								</h1>
								{childrenWithProps}
							</>
						)}
					</div>
				</div>
			</CSSTransition>,
			el
		);
	} else {
		return null;
	}
};

export default GlobalModal;
